import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { trpc, createTrpcClient } from './api/index.js'
import { ToastProvider, useToast } from './components/common/Toast.js'
import { ThemeProvider } from './components/ThemeProvider.js'
import { Sidebar } from './components/layout/Sidebar.js'
import { LoginScreen } from './components/auth/LoginScreen.js'
import { GlobalSearch } from './components/common/GlobalSearch.js'
import { ErrorBoundary, reincarcaOData } from './components/common/ErrorBoundary.js'
import {
  useAuthStore, registerLogoutCleanup, registerSessionScopeReset, AUTH_STORAGE_KEY, tokenDinSesiuneSalvata,
  marcheazaSesiuneInvechita,
} from './stores/authStore.js'
import { useNav, type Page } from './stores/navigationStore.js'
import { useAlertsStore } from './stores/alertsStore.js'
import { useComutaPunctLucru } from './lib/comutaPunctLucru.js'
import { humanizeError } from './lib/utils.js'
import { Menu, Search, Building2 } from 'lucide-react'

// Lazy-loaded pages
const DashboardPage    = lazy(() => import('./pages/dashboard/DashboardPage.js'))
const ClientiPage      = lazy(() => import('./pages/clienti/ClientiPage.js'))
const EchipamentePage  = lazy(() => import('./pages/echipamente/EchipamentePage.js'))
const RezervariPage    = lazy(() => import('./pages/rezervari/RezervariPage.js'))
const ContractePage    = lazy(() => import('./pages/contracte/ContractePage.js'))
const PredareReturPage = lazy(() => import('./pages/predare-retur/PredareReturPage.js'))
const FacturarePage    = lazy(() => import('./pages/facturare/FacturarePage.js'))
const IncasariPage     = lazy(() => import('./pages/incasari/IncasariPage.js'))
const SetariPage       = lazy(() => import('./pages/setari/SetariPage.js'))
const HardwarePage     = lazy(() => import('./pages/hardware/HardwarePage.js'))
const MentenantaPage   = lazy(() => import('./pages/mentenanta/MentenantaPage.js'))
const RapoartePage     = lazy(() => import('./pages/rapoarte/RapoartePage.js'))
const CheltuieliPage   = lazy(() => import('./pages/cheltuieli/CheltuieliPage.js'))
const TransferPage     = lazy(() => import('./pages/transfer/TransferPage.js'))

const PAGE_LABELS: Record<Page, string> = {
  dashboard:     'Dashboard',
  clienti:       'Clienți',
  echipamente:   'Inventar',
  contracte:     'Contracte',
  'predare-retur': 'Predare / Retur',
  facturare:     'Facturare',
  incasari:      'Încasări',
  cheltuieli:    'Cheltuieli',
  transfer:      'Transfer',
  mentenanta:    'Mentenanță',
  rezervari:     'Rezervări',
  rapoarte:      'Rapoarte',
  hardware:      'Hardware & AMEF',
  setari:        'Configurări',
}

const PAGE_SHORTCUTS: Record<string, Page> = {
  '1': 'dashboard', '2': 'clienti', '3': 'echipamente',
  '4': 'contracte', '5': 'facturare', '6': 'rezervari',
  '7': 'predare-retur', '8': 'mentenanta', '9': 'rapoarte',
}

function Placeholder({ name }: { name: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-32 text-slate-500">
      <p className="text-lg font-semibold">{name}</p>
      <p className="text-sm mt-2 text-slate-600">În curs de implementare</p>
    </div>
  )
}

function PageRouter() {
  const { page } = useNav()
  switch (page) {
    case 'dashboard':     return <DashboardPage />
    case 'clienti':       return <ClientiPage />
    case 'echipamente':   return <EchipamentePage />
    case 'rezervari':     return <RezervariPage />
    case 'contracte':     return <ContractePage />
    case 'predare-retur': return <PredareReturPage />
    case 'facturare':     return <FacturarePage />
    case 'incasari':      return <IncasariPage />
    case 'cheltuieli':    return <CheltuieliPage />
    case 'transfer':      return <TransferPage />
    case 'mentenanta':    return <MentenantaPage />
    case 'rapoarte':      return <RapoartePage />
    case 'hardware':      return <HardwarePage />
    case 'setari':        return <SetariPage />
    default:              return <Placeholder name={page} />
  }
}

// ─── Mobile TopBar ─────────────────────────────────────────────────────────

const ROL_AVATAR: Record<string, string> = {
  admin:       'bg-red-900/40 border-red-800/50 text-red-200',
  operator:    'bg-blue-900/40 border-blue-800/50 text-blue-200',
  casier:      'bg-green-900/40 border-green-800/50 text-green-200',
  vizualizare: 'bg-slate-800 border-slate-700 text-slate-400',
}

function MobileTopBar({ onMenuOpen, onSearchOpen }: { onMenuOpen: () => void; onSearchOpen: () => void }) {
  const { page } = useNav()
  const { session } = useAuthStore()

  const initials = session?.numeComplet
    ?.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() ?? '?'
  const avatarClass = ROL_AVATAR[session?.rol ?? ''] ?? 'bg-slate-800 border-slate-700 text-slate-300'

  return (
    <header className="lg:hidden flex items-center gap-3 px-4 h-14 bg-slate-950 border-b border-slate-800/60 shrink-0">
      <button
        onClick={onMenuOpen}
        className="p-2 -ml-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors active:scale-95"
        aria-label="Deschide meniu"
      >
        <Menu size={21} />
      </button>

      <p className="flex-1 text-[15px] font-semibold text-white truncate">
        {PAGE_LABELS[page] ?? page}
      </p>

      {/* Căutarea globală era accesibilă doar prin Ctrl+K — pe telefon nu exista nicio cale. */}
      <button
        onClick={onSearchOpen}
        className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors active:scale-95"
        aria-label="Căutare globală"
      >
        <Search size={19} />
      </button>

      <div aria-hidden className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold border ${avatarClass}`}>
        {initials}
      </div>
    </header>
  )
}

// ─── App Inner ─────────────────────────────────────────────────────────────

/**
 * Doar codul tRPC decide. Potrivirea pe text („unauthorized" în mesaj) deconecta utilizatorul
 * și la erori care doar CONȚINEAU cuvântul — de ex. un FORBIDDEN cu mesaj explicativ.
 */
function isUnauthorized(error: unknown): boolean {
  const data = (error as { data?: { code?: unknown } } | null)?.data
  return data?.code === 'UNAUTHORIZED'
}

function laEroareGlobala(error: unknown) {
  if (isUnauthorized(error) && useAuthStore.getState().session) useAuthStore.getState().logout()
}

// Handler-ele GLOBALE stau pe cache-uri, nu în `defaultOptions`: în React Query v5 un
// `onError` pus pe o mutație anume ÎNLOCUIEȘTE complet pe cel implicit, deci orice mutație cu
// propriul `onError` (aproape toate) ocolea deconectarea la sesiune expirată. Callback-urile de
// pe MutationCache/QueryCache rulează MEREU, în plus față de cele locale.
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: laEroareGlobala }),
  mutationCache: new MutationCache({ onError: laEroareGlobala }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, throwOnError: false },
  },
})

// La logout, golim tot cache-ul React Query — previne scurgerea datelor între sesiuni/utilizatori
registerLogoutCleanup(() => queryClient.clear())

// La comutarea punctului de lucru și la logout: navigarea (pagina + parametrii + istoricul) și
// alertele țin de tenantul vechi. Un `contractId` din punctul A rămas în parametri ar fi fost
// căutat în baza punctului B; istoricul „Înapoi" ar fi redeschis documente ale altui punct.
registerSessionScopeReset(() => {
  useNav.getState().reset()
  useAlertsStore.getState().reset()
})

// Token-ul de download era ținut în localStorage (comun tuturor taburilor); acum stă în
// memoria tabului. Curățăm valoarea veche rămasă de la versiunile anterioare.
try { localStorage.removeItem('fileToken') } catch { /* ignore */ }

if (typeof window !== 'undefined') {
  // Sesiunea e PER TAB (în memorie), dar localStorage e COMUN. Când alt tab comută punctul de
  // lucru, se deconectează sau intră cu alt cont, tabul acesta ar continua să afișeze vechiul
  // punct de lucru — iar orice cerere pornită de aici ar ajunge într-un context pe care
  // utilizatorul nu-l vede. Reîncărcarea aliniază tabul la sesiunea curentă, fără ambiguitate.
  window.addEventListener('storage', (e) => {
    if (e.storageArea !== localStorage) return
    if (e.key !== AUTH_STORAGE_KEY && e.key !== null) return
    const nou = e.key === null ? null : tokenDinSesiuneSalvata(e.newValue)
    const curent = useAuthStore.getState().session?.token ?? null
    if (nou === curent) return
    // ÎNTÂI blocăm orice scriere a sesiunii vechi din acest tab, apoi reîncărcăm. NU golim
    // cache-ul înainte: dacă utilizatorul anulează reîncărcarea (formular nesalvat), tabul își
    // păstrează ecranul; reîncărcarea însăși pornește oricum cu memoria goală.
    marcheazaSesiuneInvechita()
    window.location.reload()
  })

  // Vite emite `vite:preloadError` când un chunk dinamic (pagină lazy) nu mai există pe server —
  // tipic imediat după un deploy. Reîncărcăm o singură dată; dacă tot eșuează, ErrorBoundary
  // afișează mesajul cu butonul „Reîncarcă" (fără buclă de reload-uri).
  window.addEventListener('vite:preloadError', (e) => {
    if (reincarcaOData()) e.preventDefault()
  })
}

/**
 * Deep-link (din email) către un document al ALTUI punct de lucru decât cel al sesiunii.
 * Documentul stă în baza de date a punctului său; deschis aici, pagina nu-l găsea și nu spunea
 * de ce. Cerem explicit comutarea, cu numele punctului.
 */
function DeepLinkAltPunct({ plTinta }: { plTinta: string }) {
  const { page, params, navigate, reset } = useNav()
  const toast = useToast()
  const puncteQ = trpc.auth.getPuncteLucruAccesibile.useQuery()
  const punct = (puncteQ.data ?? []).find(p => p.id === plTinta) as { id: string; denumire: string; codul?: string | null } | undefined
  const tinta = { page, params }
  const comuta = useComutaPunctLucru({
    onSuccess: () => navigate(tinta.page, tinta.params),
    onError: (e) => toast.error(humanizeError((e as { message?: string } | null)?.message)),
  })

  return (
    <div role="alert" className="max-w-lg mt-16 sm:mt-24 mx-4 sm:mx-auto bg-slate-900 border border-amber-700/50 rounded-2xl p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-900/30 border border-amber-700/40 flex items-center justify-center shrink-0">
          <Building2 size={18} className="text-amber-400" />
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-white">Documentul aparține altui punct de lucru</p>
          {puncteQ.isLoading ? (
            <p className="text-sm text-slate-400 mt-1">Se verifică punctul de lucru…</p>
          ) : punct ? (
            <p className="text-sm text-slate-300 mt-1 leading-relaxed">
              Documentul aparține punctului de lucru <strong className="text-white">{punct.codul ? `${punct.codul} — ` : ''}{punct.denumire}</strong>.
              Comută punctul de lucru pentru a-l deschide.
            </p>
          ) : (
            <p className="text-sm text-slate-300 mt-1 leading-relaxed">
              Documentul aparține unui punct de lucru la care contul tău nu are acces. Cere administratorului accesul.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-5">
        {punct && (
          <button
            onClick={() => comuta.mutate(plTinta)}
            disabled={comuta.isPending}
            className="flex-1 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors disabled:opacity-60"
          >
            {comuta.isPending ? 'Se comută…' : `Comută pe ${punct.denumire}`}
          </button>
        )}
        <button
          onClick={() => reset()}
          className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm transition-colors"
        >
          Rămâi pe punctul curent
        </button>
      </div>
    </div>
  )
}

function AppInner() {
  const { session, setFileToken, fileToken } = useAuthStore()
  const { navigate, page, deepLinkPl, clearDeepLinkPl } = useNav()
  const [showSearch, setShowSearch] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Emite un token de download restrâns (scope 'file') pentru URL-urile /files?t= —
  // dacă e scurs în loguri NU dă acces la tRPC/sesiune (audit C-sec2). TTL 2h.
  const downloadTokenQ = trpc.auth.getDownloadToken.useQuery(undefined, {
    enabled: !!session,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 90 * 60 * 1000,
  })
  useEffect(() => {
    if (downloadTokenQ.data?.token) setFileToken(downloadTokenQ.data.token)
  }, [downloadTokenQ.data, setFileToken])
  // Sesiune nouă (parolă schimbată / sesiuni revocate) → tokenul de fișiere vechi e mort (`cv`
  // mai mic). Îl cerem din nou imediat, nu la următorul refetch programat.
  const tokenSesiune = session?.token
  const tokenSesiuneAnterior = useRef(tokenSesiune)
  useEffect(() => {
    if (tokenSesiuneAnterior.current && tokenSesiune && tokenSesiuneAnterior.current !== tokenSesiune) {
      void downloadTokenQ.refetch()
    }
    tokenSesiuneAnterior.current = tokenSesiune
  }, [tokenSesiune]) // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link pe punctul de lucru al sesiunii → nimic de reconciliat.
  const deepLinkAltPunct = !!session && !!deepLinkPl && deepLinkPl !== session.punctLucruId
  useEffect(() => {
    if (session && deepLinkPl && deepLinkPl === session.punctLucruId) clearDeepLinkPl()
  }, [session, deepLinkPl, clearDeepLinkPl])

  // Blochează scroll-ul body cât drawer-ul e deschis pe mobil
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [drawerOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        if (session) setShowSearch(s => !s)
        return
      }
      if (e.key === 'Escape') {
        setShowSearch(false)
        setDrawerOpen(false)
        return
      }
      if ((e.ctrlKey || e.metaKey) && !isInput) {
        const target = PAGE_SHORTCUTS[e.key]
        if (target) {
          // Nu naviga peste un wizard/modal deschis — ar demonta pagina și ar pierde datele
          // nesalvate din formular. Prezența unui overlay (.z-50/.z-[60]) blochează scurtătura.
          if (document.querySelector('.fixed.inset-0.z-50, .fixed.inset-0.z-\\[60\\], .fixed.inset-0.z-\\[70\\]')) return
          e.preventDefault()
          navigate(target)
          setDrawerOpen(false)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate, session])

  if (!session) return <LoginScreen />

  // URL-urile /files?t= se construiesc la randare (secureFileUrl) cu tokenul de fișiere din acel
  // moment. Fără token (prima randare după reîncărcare / comutare de punct de lucru) ieșeau fără
  // `?t=` și rămâneau așa în componentele care nu se re-randează → imagini/linkuri 401. Așteptăm
  // tokenul DOAR cât cererea e în zbor (~100 ms); dacă eșuează, randăm oricum (nu blocăm).
  const astepTokenFisiere = !fileToken && (downloadTokenQ.isLoading || downloadTokenQ.isFetching)

  return (
    // `100dvh`, nu `h-screen` (100vh): pe mobil 100vh include zona barei de adrese, deci
    // partea de jos a paginii (butoane de salvare) ajungea sub bara browserului.
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-slate-950">

      {/* Header vizibil doar pe mobil/tabletă */}
      <MobileTopBar onMenuOpen={() => setDrawerOpen(true)} onSearchOpen={() => { setDrawerOpen(false); setShowSearch(true) }} />

      {/* Corp principal: sidebar + content */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* Sidebar — drawer pe mobil, static pe desktop */}
        <Sidebar
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSearchOpen={() => setShowSearch(true)}
        />

        {/* Overlay întunecat pe mobil cât drawer-ul e deschis */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
        )}

        {/* Conținut pagini */}
        {/* `key` pe punctul de lucru: la comutare, pagina se REMONTEAZĂ — starea locală
            (filtre, rânduri expandate, formulare) a punctului vechi nu supraviețuiește. */}
        <main key={session.punctLucruId} className="flex-1 overflow-auto bg-slate-950 min-h-0">
          {deepLinkAltPunct ? (
            <DeepLinkAltPunct plTinta={deepLinkPl!} />
          ) : astepTokenFisiere ? (
            <div className="flex items-center justify-center h-full text-slate-500" aria-busy="true">
              <div className="w-6 h-6 border-2 border-slate-700 border-t-brand-500 rounded-full animate-spin" />
            </div>
          ) : (
            <ErrorBoundary resetKey={page}>
              <Suspense fallback={
                <div className="flex items-center justify-center h-full text-slate-500">
                  <div className="w-6 h-6 border-2 border-slate-700 border-t-brand-500 rounded-full animate-spin" />
                </div>
              }>
                <PageRouter />
              </Suspense>
            </ErrorBoundary>
          )}
        </main>
      </div>

      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
    </div>
  )
}

export default function App() {
  const [trpcClient] = useState(() => createTrpcClient())

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ThemeProvider>
            <AppInner />
          </ThemeProvider>
        </ToastProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}
